import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/context/AuthContext";
import { PlayerProvider } from "@/context/PlayerContext";
import { CartProvider } from "@/context/CartContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { ScrollToTop } from "@/components/ScrollToTop";
import { Toaster } from "@/components/ui/sonner";

import Home from "@/pages/Home";
import Listen from "@/pages/Listen";
import EpisodeDetail from "@/pages/EpisodeDetail";
import Books from "@/pages/Books";
import BookDetail from "@/pages/BookDetail";
import Reader from "@/pages/Reader";
import AudioDetail from "@/pages/AudioDetail";
import TheMuscleSchool from "@/pages/TheMuscleSchool";
import TMSLesson from "@/pages/TMSLesson";
import TMSDashboard from "@/pages/TMSDashboard";
import TMSActivate from "@/pages/TMSActivate";
import Gold from "@/pages/Gold";
import GoldActivate from "@/pages/GoldActivate";
import Vault from "@/pages/Vault";
import Mods from "@/pages/Mods";
import Library from "@/pages/Library";
import Guide from "@/pages/Guide";
import Account from "@/pages/Account";
import Support from "@/pages/Support";
import Search from "@/pages/Search";
import SignInPage from "@/pages/SignIn";
import SignUpPage from "@/pages/SignUp";
import ForgotPasswordPage from "@/pages/ForgotPassword";
import ResetPasswordPage from "@/pages/ResetPassword";
import CustomerDashboard from "@/pages/CustomerDashboard";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCatalog from "@/pages/admin/AdminCatalog";
import AdminImports from "@/pages/admin/AdminImports";
import AdminOffers from "@/pages/admin/AdminOffers";
import AdminJourneys from "@/pages/admin/AdminJourneys";
import AdminGrowth from "@/pages/admin/AdminGrowth";
import AdminBilling from "@/pages/admin/AdminBilling";
import AdminManuscripts from "@/pages/admin/AdminManuscripts";
import AdminMuscleSchool from "@/pages/admin/AdminMuscleSchool";
import AdminGold from "@/pages/admin/AdminGold";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminSecurityLog from "@/pages/admin/AdminSecurityLog";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Checkout from "@/pages/Checkout";
import CheckoutReturn from "@/pages/CheckoutReturn";
import NotFound from "@/pages/NotFound";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import RefundPolicy from "@/pages/RefundPolicy";
import DigitalDeliveryPolicy from "@/pages/DigitalDeliveryPolicy";
import SubscriptionPolicy from "@/pages/SubscriptionPolicy";
import PrivacyChoices from "@/pages/PrivacyChoices";
import Unsubscribe from "@/pages/Unsubscribe";

const queryClient = new QueryClient();

function LegacyLessonRedirect() {
  const { id } = useParams();
  return <Navigate to={`/themuscleschool/lessons/${id}`} replace />;
}

function App() {
  const adminChildRoutes = [
    <Route index element={<AdminDashboard />} key="admin-index" />,
    <Route path="customers" element={<AdminCustomers />} key="admin-customers" />,
    <Route path="subscriptions" element={<AdminSubscriptions />} key="admin-subscriptions" />,
    <Route path="security" element={<AdminSecurityLog />} key="admin-security" />,
    <Route path="catalog" element={<AdminCatalog />} key="admin-catalog" />,
    <Route path="imports" element={<AdminImports />} key="admin-imports" />,
    <Route path="offers" element={<AdminOffers />} key="admin-offers" />,
    <Route path="billing" element={<AdminBilling />} key="admin-billing" />,
    <Route path="manuscripts" element={<AdminManuscripts />} key="admin-manuscripts" />,
    <Route path="muscle-school" element={<AdminMuscleSchool />} key="admin-muscle-school" />,
    <Route path="gold" element={<AdminGold />} key="admin-gold" />,
    <Route path="journeys" element={<AdminJourneys />} key="admin-journeys" />,
    <Route path="growth" element={<AdminGrowth />} key="admin-growth" />,
  ];
  return (
    <div className="App">
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <PlayerProvider>
              <CartProvider>
              <ScrollToTop />
              <Toaster theme="dark" />
              <Routes>
                <Route path="/sign-in" element={<SignInPage />} />
                <Route path="/sign-up" element={<SignUpPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/reader/:bookId" element={<Reader />} />
                <Route element={<AppLayout />}>
                  <Route index element={<Home />} />
                  <Route path="/listen" element={<Listen />} />
                  <Route path="/listen/:id" element={<EpisodeDetail />} />
                  <Route path="/books" element={<Books />} />
                  <Route path="/read/:id" element={<BookDetail />} />
                  <Route path="/audio/:id" element={<AudioDetail />} />
                  <Route path="/themuscleschool" element={<TheMuscleSchool />} />
                  <Route path="/themuscleschool/lessons/:id" element={<TMSLesson />} />
                  <Route path="/themuscleschool/dashboard" element={<TMSDashboard />} />
                  <Route path="/themuscleschool/activate" element={<TMSActivate />} />
                  <Route path="/learn" element={<Navigate to="/themuscleschool" replace />} />
                  <Route path="/learn/lessons/:id" element={<LegacyLessonRedirect />} />
                  <Route path="/gold" element={<Gold />} />
                  <Route path="/vault" element={<Vault />} />
                  <Route path="/vault/activate" element={<GoldActivate />} />
                  <Route path="/mods" element={<Mods />} />
                  <Route path="/library" element={<Library />} />
                  <Route path="/dashboard" element={<CustomerDashboard />} />
                  <Route path="/guide" element={<Guide />} />
                  <Route path="/account" element={<Account />} />
                  <Route path="/account/billing" element={<Account />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/checkout/return" element={<CheckoutReturn />} />
                  <Route path="/payment/success" element={<PaymentSuccess />} />
                  <Route path="/payment/cancel" element={<PaymentCancel />} />
                  <Route path="/support" element={<Support />} />
                  <Route path="/search" element={<Search />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/refund-policy" element={<RefundPolicy />} />
                  <Route path="/digital-delivery-policy" element={<DigitalDeliveryPolicy />} />
                  <Route path="/subscription-policy" element={<SubscriptionPolicy />} />
                  <Route path="/privacy-choices" element={<PrivacyChoices />} />
                  <Route path="/unsubscribe" element={<Unsubscribe />} />
                  <Route path="/admin" element={<AdminLayout />}>
                    {adminChildRoutes}
                  </Route>
                  <Route path="/operator" element={<AdminLayout />}>
                    {adminChildRoutes}
                  </Route>
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
              </CartProvider>
            </PlayerProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </div>
  );
}

export default App;
